import { internalGetReq } from "../api/requestFunctions";
import { API_AUTH_TYPES, API_ROUTES } from "../api/apiConstants";
import { AUTHORIZATION } from "../constants";

/**
 * Checks whether the current user has advisor or admin status.
 * The access token must already be in localStorage before calling.
 * @returns {Promise<{ is_admin: boolean, is_advisor: boolean, is_manager: boolean }>}
 */
export const authenticateAdvisorAdmin = async () => {
  try {
    // Token should be stored in localStorage before calling this
    const response = await internalGetReq(
      API_AUTH_TYPES.USER,
      API_ROUTES.AUTH,
      "admin_advisor"
    );
    return response;
  } catch (error) {
    throw new Error(error.message);
  }
};

/**
 * Checks whether the current user passes a specific auth endpoint.
 * @param {string} endpoint - The auth endpoint to check (e.g. "is_content_editor")
 * @returns {Promise<boolean>} True if the request succeeds (authorized), false otherwise
 */
export const getAuthStatus = async (endpoint) => {
  try {
    // Token should be stored in localStorage before calling this
    await internalGetReq(
      API_AUTH_TYPES.USER,
      API_ROUTES.AUTH,
      endpoint
    );
    return true;
  } catch (error) {
    return false;
  }
};

/** @returns {Promise<boolean>} True if the current user is an admin */
const authIsAdmin = async () => {
  const auth = await authenticateAdvisorAdmin();
  return auth.is_admin;
};

/** @returns {Promise<boolean>} True if the current user is an advisor */
const authIsAdvisor = async () => {
  const auth = await authenticateAdvisorAdmin();
  return auth.is_advisor;
};

/**
 * Checks authorization for the given role type.
 * @param {string} authorizationType - One of the AUTHORIZATION constants (e.g. AUTHORIZATION.ADMIN)
 * @returns {Promise<boolean>} True if the current user has the specified role
 * @throws {Error} If authorizationType is not a recognized value
 */
export const authenticateAuth = async (authorizationType) => {
  if (authorizationType === AUTHORIZATION.ADMIN) {
    return await authIsAdmin();
  }
  if (authorizationType === AUTHORIZATION.ADVISOR) {
    return authIsAdvisor();
  }
  if (authorizationType === AUTHORIZATION.CONTENT_EDITOR) {
    return getAuthStatus("is_content_editor");
  }
  if (authorizationType === AUTHORIZATION.BLOG_EDITOR) {
    return getAuthStatus("is_blog_editor");
  }
  if (authorizationType === AUTHORIZATION.TRAVEL_LOUNGE_EDITOR) {
    return getAuthStatus("is_travel_lounge_editor");
  }
  if (authorizationType === AUTHORIZATION.LEARNING_LAB_EDITOR) {
    return getAuthStatus("is_learning_lab_editor");
  }
  throw new Error("Unknown authentication");
};
